import { useState, useEffect, useCallback, ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LogoIcon } from '@localstack/ui';
import { UserService, storeAuthToken, formatError, formatErrorCode } from '@localstack/services';
import { Box, Link, Typography } from '@mui/material';

import { SSOPostMessageErrorPayload, SSOPostMessageSuccessPayload } from '@localstack/types';

import { useAuthProvider } from '~/hooks/useAuthProvider';
import { BASE_URL, AppRoute } from '~/config';

import { AuthLayout } from '../Account/AuthLayout';

const generateIdpErrorPayload = (error: string, code?: string): SSOPostMessageErrorPayload => ({
  messageType: 'sso-error',
  error: {
    type: 'idp-error',
    message: error,
    code,
  },
});

type CognitoError = {
  error: string;
  errorDescription?: string;
};

type CallbackURLParameters = {
  code: string;
};

const CODE_PARAM = 'code';

// Needed to overcome LS bug https://github.com/localstack/localstack-ext/issues/4796
const getCallbackUrlParameters = (url: string, searchParams: URLSearchParams): CallbackURLParameters => {
  // list of substring that if present in URL means that we are using LocalStack and not AWS
  const LS_INDICATORS = ['.sandbox.localstack.cloud', 'http://localhost:3000', 'http://127.0.0.1:3000'];
  const params = LS_INDICATORS.some((lsIndicator) => url.includes(lsIndicator))
    ? new URLSearchParams(new URL(url.replace(/\?([^=]+=[^&?]+)\?/, '?$1&')).search)
    : searchParams;

  return {
    code: params.get(CODE_PARAM) ?? '',
  };
};

export const SSOCallback = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description') || undefined;
  const cognitoError: Optional<CognitoError> = error ? { error, errorDescription } : undefined;

  const [loading, setLoading] = useState(true);
  const [errorFromCognito, setErrorFromCognito] = useState<Optional<CognitoError>>(cognitoError);
  const [goodToGo, setGoodToGo] = useState(false);

  const { reloadUserInfo } = useAuthProvider();

  const { code } = getCallbackUrlParameters(window.location.href, searchParams);

  const authenticate = useCallback(async () => {
    setErrorFromCognito(null);
    setLoading(true);

    const redirect_uri = BASE_URL + AppRoute.SSO_CALLBACK;

    try {
      const response = await UserService.finalizeSso({ code: code || '', redirect_uri });

      storeAuthToken(response);
      await reloadUserInfo();
      setGoodToGo(true);

      const payload: SSOPostMessageSuccessPayload = { messageType: 'sso-success' };
      window.opener.postMessage(payload, BASE_URL);
    } catch (err) {
      const errorMessage = formatError(err as Error);
      const payload = generateIdpErrorPayload(errorMessage, formatErrorCode(err as Error) ?? undefined);

      window.opener.postMessage(payload, BASE_URL);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    if (errorFromCognito) {
      const payload = generateIdpErrorPayload(
        `
        ${errorFromCognito.error}
        ${errorFromCognito.errorDescription ? `(${errorFromCognito.errorDescription.trim()})` : ''}`,
        errorFromCognito.errorDescription?.trim(),
      );
      window.opener.postMessage(payload, BASE_URL);
      return;
    }
    authenticate();
  }, []);

  return (
    <AuthLayout documentTitle="SSO Login">
      <Box textAlign="center">
        <Box mb={4}>
          <LogoIcon />
        </Box>
        <Typography variant="h3" component="h2" fontWeight={600}>
          Finalizing your Login
        </Typography>
        <Box mt={4}>
          {!errorFromCognito && code && (
            <Typography fontSize={13} color="text.tertiary" fontWeight={500}>
              {loading && code && 'Please wait while we are finalizing your login...'}
              {goodToGo && (
                <>
                  We are all set! You will be redirected shortly... If this does not happen, please{' '}
                  <Link underline="hover" color="text.primary" onClick={() => self.close()}>
                    click here
                  </Link>
                  .
                </>
              )}
            </Typography>
          )}
        </Box>
      </Box>
    </AuthLayout>
  );
};
